import React, { FC } from 'react'
import { Settings } from 'react-slick'

import { RewardsLevel, RewardsQuery } from '../../../../../../../graphql/search'
import useMediaQuery from '../../../../../../../hooks/useMediaQuery'
import { rewardsLevels, tierFromRewardsLevel } from '../../../../../../../lib/rewards/levels'
import * as Styled from './styled'
import { TierTooltip } from './Tooltip'

export interface TierProgressBarProps {
  currentSnapshot?: NonNullable<
    NonNullable<RewardsQuery['customer']>['rewardsSnapshots']
  >['nodes'][number]
}

const totalLevels = rewardsLevels.length

const initialSlideByShown = ({
  currentTier: currentSlide,
  slidesToShow,
}: {
  currentTier: number
  slidesToShow: number
}) => {
  const totalSlides = totalLevels
  if (slidesToShow === totalSlides) {
    return 1
  }
  if (currentSlide === 1) {
    return 1
  }
  if (currentSlide === totalSlides) {
    return totalSlides - slidesToShow + 1
  }
  const slidesShownLast = Math.min(totalSlides, currentSlide + slidesToShow - 1)
  return Math.max(1, slidesShownLast - slidesToShow + 1)
}

export const TierProgressBar: FC<TierProgressBarProps> = ({ currentSnapshot }) => {
  const currentTier = tierFromRewardsLevel(currentSnapshot?.level ?? RewardsLevel.Level_1)
  const isDesktop = useMediaQuery({ minWidth: '1024px' })
  const isTablet = useMediaQuery({ minWidth: '768px' })
  const isMobile = useMediaQuery({ maxWidth: '768px' })
  const slidesToShow = isDesktop ? 6 : isTablet ? 4 : isMobile ? 2 : null

  // initialSlide changes don't rerender the slider, son don't show on first mount
  if (currentTier == null || slidesToShow == null) {
    return null
  }

  const canSlide = slidesToShow < 6
  const initialSlide = initialSlideByShown({ currentTier, slidesToShow }) - 1
  const settings: Settings = {
    dots: false,
    infinite: false,
    speed: 500,
    draggable: canSlide,
    swipeToSlide: canSlide,
    arrows: false,
    slidesToScroll: 1,
    initialSlide,
    slidesToShow,
  }

  return (
    <Styled.ProgressBar>
      <Styled.Slider {...settings}>
        {rewardsLevels.map((level, index) => {
          const tier = tierFromRewardsLevel(level)
          if (tier == null) {
            return null
          }
          const first = index === 0
          const last = index === totalLevels - 1
          const filled = tier <= currentTier
          const current = tier === currentTier
          return (
            <Styled.Step key={tier} $filled={filled}>
              <TierTooltip level={level}>
                <Styled.StepLabel $current={current}>Tier {tier}</Styled.StepLabel>
              </TierTooltip>
              <Styled.StepContainer>
                <TierTooltip level={level}>
                  <Styled.StepCircle $filled={filled} />
                </TierTooltip>
                {index < totalLevels && (
                  <Styled.StepLine
                    $filled={filled}
                    $current={current}
                    $first={first}
                    $last={last}
                  />
                )}
              </Styled.StepContainer>
            </Styled.Step>
          )
        })}
      </Styled.Slider>
    </Styled.ProgressBar>
  )
}
